import { useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Wizard, useToast } from '../organisms';
import { formatPhone } from '../lib/formatPhone';
import { STATE_SELECT_OPTIONS, US_STATES } from '../lib/us-states';

/**
 * ClientSetupWizard — /clients/new, the "New business" registration wizard.
 *
 * Registers businesses on the IGNITE! Central register through ONE endpoint,
 * `POST /api/workflow/setup-client` (business + primary contact + assignments
 * in a single transaction), in either of two modes:
 *
 *   One business — the 3-step form: identity → primary contact → team & intake.
 *   Several      — a CSV of the SAME fields, previewed row by row, then created
 *                  one POST per row through that same endpoint.
 *
 * `client`/`clients` survives in the identifiers here because that is still the
 * table name (and the route, the query key, and the endpoint). Everything a
 * user reads says "business" — the register's own word for the small-business
 * owners, entrepreneurs and nonprofit leaders it serves.
 *
 * The bulk path deliberately does NOT go through the staged-import pipeline:
 * a business is a register relationship, created explicitly by a person who
 * decided to enrol it, not staged and reviewed like a bulk record load.
 *
 * The CSV columns are `Object.keys(CLIENT_WIZARD_DEFAULTS)` — literally the
 * fields the form collects — so the template can't drift from the form. Add a
 * field to `WizardState` and it appears in the template, in the preview, and in
 * the payload without a second edit.
 *
 * Additional contacts are deferred to the business detail page's Contacts tab —
 * keeps the wizard linear + scannable and lets staff add people incrementally
 * instead of front-loading every one at registration time.
 */

interface UsersListResponse {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'super_admin' | 'admin' | 'agent' | 'analyst';
}

interface OrgResponse {
  id: string;
  current_system_year: number | null;
}

interface SetupClientResponse {
  client: { id: string; name: string };
}

interface WizardState {
  // Identity
  name: string;
  short_name: string;
  category: string;
  state: string;
  city: string;
  // Primary contact
  primary_first_name: string;
  primary_last_name: string;
  primary_email: string;
  primary_phone: string;
  primary_title: string;
  // Team & intake
  assigned_admin_user_id: string;
  assigned_agent_user_id: string;
  year: string;
  type: string;
}

/**
 * The fields this wizard collects, with the value used when a field is left
 * alone. Exported because it IS the CSV contract: the template header, the
 * per-column validation, and the blank-cell fallback all derive from it.
 */
export const CLIENT_WIZARD_DEFAULTS: WizardState = {
  // Sector has no safe default — the register's largest sector is 9% of it, so
  // pre-picking one would mis-file nine businesses in ten. Blank is a real
  // state here: over half the existing register carries no sector at all.
  name: '', short_name: '', category: '', state: 'AL', city: '',
  primary_first_name: '', primary_last_name: '', primary_email: '', primary_phone: '', primary_title: '',
  assigned_admin_user_id: '', assigned_agent_user_id: '',
  year: '', type: 'Participant',
};

const EMPTY = CLIENT_WIZARD_DEFAULTS;

type FieldKey = keyof WizardState;

/** CSV header row — one column per collected field, in form order. */
export const CLIENT_CSV_COLUMNS = Object.keys(CLIENT_WIZARD_DEFAULTS) as FieldKey[];

/** The fields `setup-client` refuses without (mirrors the form's own gating). */
const REQUIRED_FIELDS: FieldKey[] = [
  'name', 'short_name', 'primary_first_name', 'primary_last_name', 'primary_email',
];

/** Human labels — used by the form's Field captions AND by CSV problem text. */
export const FIELD_LABELS: Record<FieldKey, string> = {
  name: 'Business Name',
  short_name: 'Short Name',
  category: 'Sector',
  state: 'State',
  city: 'City',
  primary_first_name: 'First Name',
  primary_last_name: 'Last Name',
  primary_email: 'Email',
  primary_phone: 'Phone',
  primary_title: 'Title',
  assigned_admin_user_id: 'Assigned Admin',
  assigned_agent_user_id: 'Assigned Coach',
  year: 'Intake Year',
  type: 'Relationship',
};

// Option vocabularies — one declaration feeding both the form's Selects and the
// CSV validator, so a CSV can never be judged against a list the form doesn't offer.

/**
 * Sectors — the twenty-one most-used values on the existing register, written
 * VERBATIM, parentheticals and all.
 *
 * Verbatim matters twice over. A picker that offered a tidied-up "Health &
 * Beauty" would write a second string for a sector the register already spells
 * out, and every count, filter and cohort roll-up would then have to know about
 * both. And because this same list judges CSV cells, label and value are kept
 * identical: a cell holding exactly what the dropdown shows must be accepted.
 *
 * The register carries 135 distinct sector strings, most of them one-offs and
 * near-duplicates of these. A row whose sector isn't on this list is flagged
 * for a human rather than written through — which is how 135 stops growing.
 */
const CATEGORY_OPTIONS = [
  'Accounting (Accountant, Bookkeeper, Tax Preparer, etc)',
  'Agriculture',
  'Arts and Crafts',
  'Child Care',
  'Cleaning Services',
  'Construction',
  'Consulting',
  'Education',
  'Entertainment',
  'Events',
  'Fashion',
  'Food & Beverage',
  'Health & Beauty (Barber, Hair Stylist, Massage Therapist, Personal Care, Nail Salons)',
  'Healthcare',
  'Marketing and PR Services',
  'Not For Profit',
  'Real Estate',
  'Retail',
  'Software as a Service (SaaS)',
  'Transportation',
  'Other', // catch-all, kept last rather than in alphabetical position
].map(s => ({ value: s, label: s }));

/**
 * Relationship — how this organisation stands to IGNITE! Central. Registering
 * a business almost always means enrolling a participant; the rest are the
 * ecosystem side of the same register.
 *
 * Deliberately narrower than what the migrated data holds: the imported rows
 * also carry Household, Customer, Reseller and Inactive, which are Salesforce
 * account-type leftovers rather than anything this product does (and "Inactive"
 * duplicates `status`). Offering them would keep them alive.
 */
const TYPE_OPTIONS = [
  { value: 'Participant', label: 'Participant' },
  { value: 'Coalition Member', label: 'Coalition Member' },
  { value: 'Referral Partner', label: 'Referral Partner' },
  { value: 'Prospect', label: 'Prospect' },
];
const ENUM_FIELDS: Partial<Record<FieldKey, { value: string }[]>> = {
  category: CATEGORY_OPTIONS,
  type: TYPE_OPTIONS,
};

/** A filled-in example row, so the template shows the shape rather than describing it. */
const CSV_SAMPLE_ROW: WizardState = {
  name: 'Magic City Catering Co.',
  short_name: 'Magic City Catering',
  category: 'Food & Beverage',
  state: 'AL',
  city: 'Birmingham',
  primary_first_name: 'Jasmine',
  primary_last_name: 'Reed',
  primary_email: 'jasmine@magiccitycatering.com',
  primary_phone: '205-555-0134',
  primary_title: 'Owner',
  assigned_admin_user_id: '',
  assigned_agent_user_id: '',
  year: '',
  type: 'Participant',
};

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The downloadable template: the collected fields, plus one example row. */
export function buildClientCsvTemplate(): string {
  const header = CLIENT_CSV_COLUMNS.map(csvCell).join(',');
  const sample = CLIENT_CSV_COLUMNS.map(c => csvCell(CSV_SAMPLE_ROW[c])).join(',');
  return `${header}\n${sample}\n`;
}

/** RFC 4180-ish reader: quoted fields, escaped quotes, CRLF or LF line ends. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop blank lines — a trailing newline shouldn't read as an empty business.
  return rows.filter(r => r.some(c => c.trim() !== ''));
}

/**
 * Read a picked file as text. `Blob.text()` isn't universal (older Safari, and
 * the jsdom build the component tests run in), so fall back to FileReader —
 * which every browser that can render this app has.
 */
function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read that file.'));
    reader.readAsText(file);
  });
}

const normalizeHeader = (s: string) => s.trim().toLowerCase().replace(/[\s-]+/g, '_');

/** Header cell → field key. Accepts the machine column or its human label. */
const HEADER_ALIASES: Record<string, FieldKey> = (() => {
  const map: Record<string, FieldKey> = {};
  for (const key of CLIENT_CSV_COLUMNS) {
    map[normalizeHeader(key)] = key;
    map[normalizeHeader(FIELD_LABELS[key])] = key;
  }
  return map;
})();

export interface CsvPreviewRow {
  /** 1-based position among the DATA rows — the number the user sees in their sheet. */
  rowNumber: number;
  values: WizardState;
  problems: string[];
}

export interface CsvParseResult {
  rows: CsvPreviewRow[];
  fileProblems: string[];
  ignoredColumns: string[];
}

function resolveUserReference(raw: string, users: UsersListResponse[]): { id: string; problem?: string } {
  const needle = raw.trim().toLowerCase();
  const match = users.find(u => u.id.toLowerCase() === needle || (u.email || '').toLowerCase() === needle);
  if (match) return { id: match.id };
  // Only claim "unknown" once the team list has actually loaded; otherwise every
  // row would be flagged by a check that has nothing to compare against.
  if (users.length > 0) return { id: '', problem: 'no user with that id or email' };
  return { id: raw.trim() };
}

/**
 * Turn CSV text into preview rows. Every problem is attached to the row it came
 * from — a bad row is shown and skipped, never silently dropped and never
 * fatal to the rows around it.
 */
export function buildCsvRows(
  text: string,
  defaults: WizardState,
  users: UsersListResponse[],
): CsvParseResult {
  const grid = parseCsv(text);
  if (grid.length === 0) {
    return { rows: [], fileProblems: ['This file has no rows.'], ignoredColumns: [] };
  }

  const headerCells = grid[0];
  const ignoredColumns: string[] = [];
  const columnAt: (FieldKey | null)[] = headerCells.map(cell => {
    const key = HEADER_ALIASES[normalizeHeader(cell)];
    if (!key && cell.trim() !== '') ignoredColumns.push(cell.trim());
    return key ?? null;
  });

  const fileProblems: string[] = [];
  const present = new Set(columnAt.filter(Boolean) as FieldKey[]);
  const missingRequired = REQUIRED_FIELDS.filter(f => !present.has(f));
  if (missingRequired.length > 0) {
    fileProblems.push(
      `The file has no ${missingRequired.map(f => FIELD_LABELS[f]).join(', ')} column — download the template and use its header row.`,
    );
  }

  const rows: CsvPreviewRow[] = grid.slice(1).map((cells, i) => {
    const values: WizardState = { ...defaults };
    const problems: string[] = [];

    columnAt.forEach((key, col) => {
      if (!key) return;
      const raw = (cells[col] ?? '').trim();
      if (raw === '') return; // blank cell → the form's own default
      values[key] = raw;
    });

    for (const field of REQUIRED_FIELDS) {
      if (values[field].trim() === '') problems.push(`${FIELD_LABELS[field]} is required.`);
    }

    if (values.primary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.primary_email)) {
      problems.push(`“${values.primary_email}” is not a valid email address.`);
    }

    if (values.state) {
      const code = values.state.toUpperCase();
      if (!(US_STATES as readonly string[]).includes(code)) {
        problems.push(`“${values.state}” is not a US state code.`);
      } else {
        values.state = code;
      }
    }

    for (const [field, options] of Object.entries(ENUM_FIELDS) as [FieldKey, { value: string }[]][]) {
      const raw = values[field];
      if (!raw) continue;
      const match = options.find(o => o.value.toLowerCase() === raw.toLowerCase());
      if (match) values[field] = match.value;
      else problems.push(`${FIELD_LABELS[field]} “${raw}” is not one of: ${options.map(o => o.value).join(', ')}.`);
    }

    if (values.year && !/^\d{4}$/.test(values.year)) {
      problems.push(`${FIELD_LABELS.year} “${values.year}” is not a four-digit year.`);
    }

    if (values.primary_phone) values.primary_phone = formatPhone(values.primary_phone);

    for (const field of ['assigned_admin_user_id', 'assigned_agent_user_id'] as FieldKey[]) {
      const raw = values[field];
      if (!raw) continue;
      const resolved = resolveUserReference(raw, users);
      values[field] = resolved.id;
      if (resolved.problem) problems.push(`${FIELD_LABELS[field]} “${raw}” — ${resolved.problem}.`);
    }

    return { rowNumber: i + 1, values, problems };
  });

  return { rows, fileProblems, ignoredColumns };
}

/** The one request body both modes send — the single-business contract, per row. */
function buildSetupClientPayload(values: WizardState) {
  // Endpoint expects { client: {...}, contacts: [...] } — `client` is the table,
  // and the payload keys are its columns.
  return {
    client: {
      name: values.name,
      short_name: values.short_name,
      status: 'active' as const,
      type: values.type || null,
      category: values.category || null,
      state: values.state || null,
      city: values.city || null,
      country: 'US',
      assigned_admin_user_id: values.assigned_admin_user_id || null,
      assigned_agent_user_id: values.assigned_agent_user_id || null,
      year: values.year ? Number(values.year) : null,
    },
    contacts: [{
      role: 'primary',
      first_name: values.primary_first_name,
      last_name: values.primary_last_name,
      email: values.primary_email,
      phone: values.primary_phone || null,
      title: values.primary_title || null,
    }],
  };
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return err instanceof Error ? err.message : fallback;
}

type SetupMode = 'single' | 'bulk';

export function ClientSetupWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [mode, setMode] = useState<SetupMode>('single');
  const [state, setState] = useState<WizardState>(EMPTY);

  // CSV mode
  const [csvFileName, setCsvFileName] = useState('');
  const [csvResult, setCsvResult] = useState<CsvParseResult | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // Business ids already created this session, keyed by CSV row number — so a
  // retry after a mid-batch failure SKIPS the rows that already succeeded
  // instead of registering each of them twice.
  const createdRef = useRef<Record<number, string>>({});

  const usersQuery = useQuery<UsersListResponse[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<UsersListResponse[]>('/api/users-list', signal),
  });

  const orgQuery = useQuery<OrgResponse[]>({
    queryKey: ['organizations'],
    queryFn: ({ signal }) => api.get<OrgResponse[]>('/api/organizations', signal),
  });

  const currentUserQuery = useQuery<{ id: string }>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<{ id: string }>('/api/current-user', signal),
  });

  // Default admin to current user once data lands
  useEffect(() => {
    if (currentUserQuery.data && !state.assigned_admin_user_id) {
      setState(s => ({ ...s, assigned_admin_user_id: currentUserQuery.data!.id }));
    }
  }, [currentUserQuery.data, state.assigned_admin_user_id]);

  // Default year once org lands
  useEffect(() => {
    if (orgQuery.data && orgQuery.data[0] && !state.year) {
      const yr = orgQuery.data[0].current_system_year || new Date().getFullYear();
      setState(s => ({ ...s, year: String(yr) }));
    }
  }, [orgQuery.data, state.year]);

  const allUsers = usersQuery.data ?? [];
  const adminUsers = allUsers.filter(u => u.role === 'super_admin' || u.role === 'admin');
  const agentUsers = allUsers.filter(u => u.role === 'agent');
  const baseYear = orgQuery.data?.[0]?.current_system_year || new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => baseYear + i);

  const set = <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => setState(s => ({ ...s, [k]: v }));

  const mutation = useMutation<SetupClientResponse, Error, void>({
    mutationFn: async () => api.post<SetupClientResponse>('/api/workflow/setup-client', buildSetupClientPayload(state)),
    onSuccess: (data) => {
      toast({ variant: 'success', message: `Business "${state.name}" registered.` });
      // Refresh the businesses list so the new one is present when staff navigate
      // back within the 60s staleTime window.
      qc.invalidateQueries({ queryKey: ['clients-list'] });
      navigate(`/clients/${data.client.id}`);
    },
    onError: (err) => {
      toast({ variant: 'error', message: errorMessage(err, 'Could not register the business.') });
    },
  });

  const canAdvance = (step: number): boolean => {
    if (step === 0) return state.name.trim().length > 0 && state.short_name.trim().length > 0;
    if (step === 1) {
      return state.primary_first_name.trim().length > 0
        && state.primary_last_name.trim().length > 0
        && state.primary_email.trim().length > 0;
    }
    return true;
  };

  // ── CSV mode ───────────────────────────────────────────────────────────────
  const csvDefaults: WizardState = {
    ...EMPTY,
    assigned_admin_user_id: currentUserQuery.data?.id ?? '',
    year: String(baseYear),
  };
  const csvRows = csvResult?.rows ?? [];
  const creatableRows = csvRows.filter(r => r.problems.length === 0);
  const skippedCount = csvRows.length - creatableRows.length;

  function downloadTemplate() {
    const blob = new Blob([buildClientCsvTemplate()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ignite-new-businesses-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function onPickCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // A new file means new row numbers — anything remembered from a previous
    // batch would skip the wrong rows.
    createdRef.current = {};
    setCsvFileName(file.name);
    setCsvResult(null);

    let text: string;
    try {
      text = await readFileText(file);
    } catch {
      toast({ variant: 'error', message: 'Could not read that file. Try choosing it again.' });
      return;
    }
    if (text.includes('\u0000') || text.startsWith('PK')) {
      toast({ variant: 'error', message: 'This looks like a binary file. Export the sheet as CSV first.' });
      return;
    }
    setCsvResult(buildCsvRows(text, csvDefaults, allUsers));
  }

  async function submitBulk() {
    setBulkSubmitting(true);
    try {
      const createdIds: string[] = [];
      for (const row of creatableRows) {
        // Reuse an id created on a prior (failed) attempt so a retry never
        // registers a second business for a row that already succeeded.
        let id = createdRef.current[row.rowNumber];
        if (!id) {
          const res = await api.post<SetupClientResponse>('/api/workflow/setup-client', buildSetupClientPayload(row.values));
          id = res.client.id;
          createdRef.current[row.rowNumber] = id;
        }
        createdIds.push(id);
      }
      qc.invalidateQueries({ queryKey: ['clients-list'] });
      createdRef.current = {};
      toast({ variant: 'success', message: `${createdIds.length} business${createdIds.length === 1 ? '' : 'es'} registered.` });
      navigate('/clients');
    } catch (err) {
      const created = Object.keys(createdRef.current).length;
      const base = errorMessage(err, 'Could not finish registering the businesses.');
      toast({
        variant: 'error',
        message: created > 0
          ? `${base} — ${created} business${created === 1 ? ' was' : 'es were'} already registered; retry to finish the rest.`
          : base,
      });
    } finally {
      setBulkSubmitting(false);
    }
  }

  const canAdvanceBulk = (step: number): boolean => {
    if (step === 0) return csvRows.length > 0;
    return creatableRows.length > 0;
  };

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>New business</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Register one business step by step, or register a batch of them from a CSV of the same fields.
        </p>
      </header>

      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-6)',
      }}>
        <ModePicker mode={mode} onChange={setMode} />

        {mode === 'single' ? (
          <Wizard
            key="single"
            steps={[
              { label: 'Identity', description: 'Business basics' },
              { label: 'Primary contact', description: 'Required' },
              { label: 'Team & intake', description: 'Review + register' },
            ]}
            onSubmit={() => mutation.mutate()}
            onCancel={() => navigate('/clients')}
            submitting={mutation.isPending}
            submitLabel="Register business"
            canAdvance={canAdvance}
          >
            {({ currentStep }) => (
              <>
                {currentStep === 0 && (
                  <StepIdentity state={state} set={set} />
                )}
                {currentStep === 1 && (
                  <StepPrimaryContact state={state} set={set} />
                )}
                {currentStep === 2 && (
                  <StepTeamAndIntake
                    state={state}
                    set={set}
                    adminUsers={adminUsers}
                    agentUsers={agentUsers}
                    yearOptions={yearOptions}
                  />
                )}
              </>
            )}
          </Wizard>
        ) : (
          <Wizard
            key="bulk"
            steps={[
              { label: 'CSV file', description: 'Template + upload' },
              { label: 'Review & register', description: 'Row by row' },
            ]}
            onSubmit={submitBulk}
            onCancel={() => navigate('/clients')}
            submitting={bulkSubmitting}
            submitLabel={`Register ${creatableRows.length} business${creatableRows.length === 1 ? '' : 'es'}`}
            canAdvance={canAdvanceBulk}
          >
            {({ currentStep }) => (
              <>
                {currentStep === 0 && (
                  <StepCsvUpload
                    fileName={csvFileName}
                    rowCount={csvRows.length}
                    onDownloadTemplate={downloadTemplate}
                    onPickFile={onPickCsv}
                  />
                )}
                {currentStep === 1 && (
                  <StepCsvReview result={csvResult} creatable={creatableRows.length} skipped={skippedCount} />
                )}
              </>
            )}
          </Wizard>
        )}
      </div>
    </>
  );
}

function ModePicker({ mode, onChange }: { mode: SetupMode; onChange: (m: SetupMode) => void }) {
  const options: { value: SetupMode; label: string; hint: string }[] = [
    { value: 'single', label: 'One business', hint: 'Fill the form step by step.' },
    { value: 'bulk', label: 'Several businesses', hint: 'Upload a CSV of the same fields.' },
  ];
  return (
    <div role="radiogroup" aria-label="How many businesses" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 'var(--sp-3)',
      marginBottom: 'var(--sp-6)',
    }}>
      {options.map(o => {
        const selected = mode === o.value;
        return (
          <label key={o.value} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--sp-2)',
            padding: 'var(--sp-3) var(--sp-4)',
            border: `1px solid ${selected ? 'var(--brand-primary)' : 'var(--color-border)'}`,
            background: selected ? 'var(--brand-primary-bg)' : 'var(--color-bg)',
            borderRadius: 'var(--r-md)',
            cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="business-setup-mode"
              value={o.value}
              checked={selected}
              onChange={() => onChange(o.value)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{o.label}</span>
              <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{o.hint}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function StepCsvUpload({ fileName, rowCount, onDownloadTemplate, onPickFile }: {
  fileName: string;
  rowCount: number;
  onDownloadTemplate: () => void;
  onPickFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div>
        <p style={{ margin: '0 0 var(--sp-2)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          The template's columns are exactly the fields this form collects. Leave a cell blank to
          take the form's own default for it.
        </p>
        <Button variant="secondary" size="sm" onClick={onDownloadTemplate}>Download CSV template</Button>
      </div>

      <Field label="CSV file" fullWidth>
        <input type="file" accept=".csv,.txt" onChange={onPickFile} style={{ fontFamily: 'inherit' }} />
      </Field>

      {fileName && (
        <p style={{ margin: 0 }}>
          <Badge variant="success" dot>{fileName}</Badge>
          <span style={{ color: 'var(--color-muted)', marginLeft: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
            {rowCount} row{rowCount === 1 ? '' : 's'} read
          </span>
        </p>
      )}

      <DocumentsNotAccepted />
    </div>
  );
}

/**
 * The place a user reaches for a document upload — so this is where the reason
 * there isn't one belongs. An option that silently vanishes teaches nobody, and
 * a control that renders disabled just looks broken.
 */
function DocumentsNotAccepted() {
  return (
    <div style={{
      padding: 'var(--sp-3) var(--sp-4)',
      background: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-md)',
    }}>
      <p style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
        Why you can't upload documents here
      </p>
      <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
        Businesses are never created from a document. A capability statement, a business licence
        or a W-9 names a legal entity — not the relationship you are registering, and not the
        person to contact, which this form requires. Documents attach to a business that already
        exists. To bring records in from files, use Import, where every row is reviewed by a
        person before anything is written.
      </p>
    </div>
  );
}

function StepCsvReview({ result, creatable, skipped }: {
  result: CsvParseResult | null;
  creatable: number;
  skipped: number;
}) {
  if (!result || result.rows.length === 0) {
    return <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No rows to review yet — choose a CSV file first.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div style={{
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        fontSize: 'var(--fs-sm)',
        color: 'var(--color-ink-2)',
      }}>
        Registering <strong>{creatable}</strong> business{creatable === 1 ? '' : 'es'} of{' '}
        <strong>{result.rows.length}</strong> row{result.rows.length === 1 ? '' : 's'}
        {skipped > 0 ? <> · <strong>{skipped}</strong> skipped until the problems below are fixed in the file</> : null}.
      </div>

      {result.fileProblems.map((p, i) => (
        <p key={i} style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-status-amber)' }}>{p}</p>
      ))}
      {result.ignoredColumns.length > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Ignored column{result.ignoredColumns.length === 1 ? '' : 's'}: {result.ignoredColumns.join(', ')}.
        </p>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              {['Row', 'Business Name', 'Short Name', 'Primary contact', 'Email', 'State', 'Status'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: 'var(--sp-2) var(--sp-3)',
                  fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)',
                  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map(row => {
              const bad = row.problems.length > 0;
              return (
                <tr key={row.rowNumber} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={cell}>{row.rowNumber}</td>
                  <td style={cell}>{row.values.name || '—'}</td>
                  <td style={cell}>{row.values.short_name || '—'}</td>
                  <td style={cell}>{[row.values.primary_first_name, row.values.primary_last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td style={cell}>{row.values.primary_email || '—'}</td>
                  <td style={cell}>{row.values.state || '—'}</td>
                  <td style={cell}>
                    {bad ? (
                      <span>
                        <Badge variant="warning" dot>skipped</Badge>
                        <span style={{ display: 'block', marginTop: 'var(--sp-1)', color: 'var(--color-ink-2)' }}>
                          {row.problems.join(' ')}
                        </span>
                      </span>
                    ) : (
                      <Badge variant="success" dot>will be created</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cell: React.CSSProperties = { padding: 'var(--sp-2) var(--sp-3)', verticalAlign: 'top', color: 'var(--color-ink)' };

function StepIdentity({ state, set }: { state: WizardState; set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void }) {
  return (
    <Grid>
      <Field label={`${FIELD_LABELS.name} *`} required>
        <Input value={state.name} onChange={e => set('name')(e.target.value)} placeholder="e.g. Magic City Catering Co." required />
      </Field>
      <Field label={`${FIELD_LABELS.short_name} *`} required>
        <Input value={state.short_name} onChange={e => set('short_name')(e.target.value)} placeholder="e.g. Magic City Catering" required />
      </Field>
      <Field label={FIELD_LABELS.category}>
        <Select
          value={state.category}
          onChange={e => set('category')(e.target.value)}
          placeholder="— Select sector —"
          options={CATEGORY_OPTIONS}
        />
      </Field>
      <Field label={FIELD_LABELS.state}>
        {/* State is a dropdown of the canonical US codes, not free text. */}
        <Select value={state.state} onChange={e => set('state')(e.target.value)} options={STATE_SELECT_OPTIONS} />
      </Field>
      <Field label={FIELD_LABELS.city}>
        <Input value={state.city} onChange={e => set('city')(e.target.value)} placeholder="Birmingham" />
      </Field>
    </Grid>
  );
}

function StepPrimaryContact({ state, set }: { state: WizardState; set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void }) {
  return (
    <Grid>
      <Field label={`${FIELD_LABELS.primary_first_name} *`} required>
        <Input value={state.primary_first_name} onChange={e => set('primary_first_name')(e.target.value)} required />
      </Field>
      <Field label={`${FIELD_LABELS.primary_last_name} *`} required>
        <Input value={state.primary_last_name} onChange={e => set('primary_last_name')(e.target.value)} required />
      </Field>
      <Field label={`${FIELD_LABELS.primary_email} *`} required>
        <Input type="email" value={state.primary_email} onChange={e => set('primary_email')(e.target.value)} required placeholder="jasmine@magiccitycatering.com" />
      </Field>
      <Field label={FIELD_LABELS.primary_phone}>
        <Input value={state.primary_phone} onChange={e => set('primary_phone')(formatPhone(e.target.value))} inputMode="tel" placeholder="XXX-XXX-XXXX" />
      </Field>
      <Field label={FIELD_LABELS.primary_title} fullWidth>
        <Input value={state.primary_title} onChange={e => set('primary_title')(e.target.value)} placeholder="Owner" />
      </Field>
    </Grid>
  );
}

/**
 * Team & intake — who at IGNITE! owns this business, how it stands to the
 * register, and the intake year the record belongs to.
 *
 * `year` is the organisation's system year (`organizations.current_system_year`)
 * and part of the (organization, name, year) uniqueness on the table, so a
 * business can be re-registered for a later intake without colliding with its
 * earlier record. It is a register year, not any kind of fiscal or tax year.
 */
function StepTeamAndIntake({
  state, set, adminUsers, agentUsers, yearOptions,
}: {
  state: WizardState;
  set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void;
  adminUsers: UsersListResponse[];
  agentUsers: UsersListResponse[];
  yearOptions: number[];
}) {
  return (
    <Grid>
      <Field label={FIELD_LABELS.assigned_admin_user_id}>
        <Select
          value={state.assigned_admin_user_id}
          onChange={e => set('assigned_admin_user_id')(e.target.value)}
          options={adminUsers.map(u => ({
            value: u.id,
            label: `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} · ${u.role}`,
          }))}
        />
      </Field>
      <Field label={FIELD_LABELS.assigned_agent_user_id}>
        <Select
          value={state.assigned_agent_user_id}
          onChange={e => set('assigned_agent_user_id')(e.target.value)}
          placeholder="— None —"
          options={agentUsers.map(u => ({
            value: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
          }))}
        />
      </Field>
      <Field label={FIELD_LABELS.year}>
        <Select
          value={state.year}
          onChange={e => set('year')(e.target.value)}
          options={yearOptions.map(y => ({ value: String(y), label: String(y) }))}
        />
      </Field>
      <Field label={FIELD_LABELS.type}>
        <Select
          value={state.type}
          onChange={e => set('type')(e.target.value)}
          options={TYPE_OPTIONS}
        />
      </Field>
    </Grid>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 'var(--sp-4)',
    }}>{children}</div>
  );
}

function Field({ label, required, fullWidth, children }: { label: string; required?: boolean; fullWidth?: boolean; children: ReactNode }) {
  return (
    <label style={{
      display: 'block',
      gridColumn: fullWidth ? '1 / -1' : undefined,
    }}>
      <span style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>
        {label}
        {required && (
          <span aria-hidden="true" style={{
            color: 'var(--color-status-red)',
            marginLeft: 2,
          }}>*</span>
        )}
      </span>
      {children}
    </label>
  );
}
